import { MetricUnit } from '@/__generated__/graphql';
import { useModalStatusMessage } from '@/hooks';
import { getUnit } from '@/utils/units';
import { useMutation } from '@apollo/client';
import ModalActions from '@components/modal/ModalActions';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { PropsWithChildren, useEffect, type FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  UPDATE_ORGANIZATION_VISIBLE_UNITS,
  UPDATE_USER_VISIBLE_UNITS,
} from '../graphql/mutations/updateVisibleUnits';
import { PreferenceEntity } from '../types';

interface Props {
  entity: PreferenceEntity;
  data: MetricUnit[];
  factoryDefaults: MetricUnit[];
}

interface FieldValues {
  visibleUnits: MetricUnit[];
}

const allUnits = Object.values(MetricUnit);

const sorted = (units: MetricUnit[]) => {
  return [...units].sort((a, b) => {
    return a.localeCompare(b);
  });
};

export const UpdateVisibleUnitsForm: FC<Props> = ({
  entity,
  data: defaultData,
  factoryDefaults,
}) => {
  const { openModalSuccessMessage, openModalErrorMessage } = useModalStatusMessage();
  const { _, i18n } = useLingui();
  const { control, handleSubmit, reset, formState } = useForm<FieldValues>({
    defaultValues: {
      visibleUnits: sorted(defaultData),
    },
  });

  const [mutateUser] = useMutation(UPDATE_USER_VISIBLE_UNITS, {
    onCompleted() {
      openModalSuccessMessage(_(msg`Se han actualizado las preferencias`));
    },
    onError(error) {
      console.log(error.message);
      openModalErrorMessage(_(msg`Ha ocurrido un error al actualizar`));
    },
  });

  const [mutateOrganization] = useMutation(UPDATE_ORGANIZATION_VISIBLE_UNITS, {
    onCompleted() {
      openModalSuccessMessage(_(msg`Se han actualizado las preferencias`));
    },
    onError(error) {
      console.log(error.message);
      openModalErrorMessage(_(msg`Ha ocurrido un error al actualizar`));
    },
  });

  useEffect(() => {
    reset({ visibleUnits: sorted(defaultData) });
  }, [defaultData, reset]);

  const getUnitLabel = (unit: MetricUnit) => {
    const label = getUnit(unit, i18n);
    if (label) {
      const { long, short } = label;
      return `${long} (${short})`;
    } else return unit;
  };

  const onSubmit = handleSubmit(async (data) => {
    const isFactory = JSON.stringify(data.visibleUnits) === JSON.stringify(factoryDefaults);
    const visibleUnits = isFactory ? null : data.visibleUnits;

    if (entity.type === 'user') {
      await mutateUser({
        variables: {
          input: {
            userId: entity.id,
            visibleUnits,
          },
        },
      });
    } else if (entity.type === 'organization') {
      await mutateOrganization({
        variables: {
          input: {
            organizationId: entity.id,
            visibleUnits,
          },
        },
      });
    }
  });

  const factoryDefaultsHandler = () => {
    reset(
      {
        visibleUnits: sorted(factoryDefaults),
      },
      {
        keepDefaultValues: true,
      },
    );
  };

  const resetHandler = () => {
    reset();
  };

  const { isDirty } = formState;

  return (
    <form onSubmit={onSubmit} style={{ height: '100%' }}>
      <Controller
        control={control}
        name="visibleUnits"
        render={({ field }) => {
          const { value, onChange } = field;

          const visibleUnits = value;
          const noVisibleUnits = allUnits.filter((unit) => !visibleUnits.includes(unit));

          return (
            <Box
              display="grid"
              gridTemplateColumns="1fr 1fr"
              gap={2}
              maxHeight="100%"
              maxWidth="100%"
              gridTemplateRows="auto 1fr auto"
            >
              <Typography fontWeight="bold">
                <Trans>Unidades visibles</Trans>
              </Typography>

              <Typography fontWeight="bold">
                <Trans>Unidades no visibles</Trans>
              </Typography>

              <UnitsBox>
                {visibleUnits.length === 0 && (
                  <Typography color="text.secondary">
                    <Trans>Sin unidades visibles</Trans>
                  </Typography>
                )}

                {visibleUnits
                  .sort((a, b) => getUnitLabel(a).localeCompare(getUnitLabel(b)))
                  .map((unit) => (
                    <UnitButton
                      key={unit}
                      label={getUnitLabel(unit)}
                      onClick={() => {
                        const newValues = visibleUnits.filter(
                          (visibleUnit) => visibleUnit !== unit,
                        );
                        onChange(sorted(newValues));
                      }}
                    />
                  ))}
              </UnitsBox>

              <UnitsBox>
                {noVisibleUnits.length === 0 && (
                  <Typography color="text.secondary">
                    <Trans>Sin unidades no visibles</Trans>
                  </Typography>
                )}

                {noVisibleUnits
                  .sort((a, b) => getUnitLabel(a).localeCompare(getUnitLabel(b)))
                  .map((unit) => (
                    <UnitButton
                      key={unit}
                      label={getUnitLabel(unit)}
                      onClick={() => {
                        const newValues = Array.from(new Set([...visibleUnits, unit]));
                        onChange(sorted(newValues));
                      }}
                    />
                  ))}
              </UnitsBox>

              <Box sx={{ gridColumn: 'span 2', mt: 1 }}>
                <ModalActions
                  dialogActionsProps={{
                    sx: {
                      mx: 0,
                      p: 0,
                    },
                  }}
                  submitLoading={formState.isSubmitting}
                  onSubmit="submit"
                  dirtyForm={isDirty}
                  customButtons={[
                    {
                      label: _(msg`Restablecer valores por defecto`),
                      onClick: factoryDefaultsHandler,
                      color: 'warning',
                    },
                  ]}
                  rightButtons={[
                    {
                      label: _(msg`Restablecer formulario`),
                      onClick: resetHandler,
                      disabled: !isDirty,
                    },
                  ]}
                />
              </Box>
            </Box>
          );
        }}
      />
    </form>
  );
};

interface UnitButtonProps {
  label: string;
  onClick: () => void;
}

const UnitButton: FC<UnitButtonProps> = ({ label, onClick }) => {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        border: 1,
        width: '100%',
        textAlign: 'left',
        borderColor: 'divider',
        p: 1,
        borderRadius: 1,
        display: 'block',
        userSelect: 'none',
        ':hover': {
          bgcolor: '#ffffff06',
        },
      }}
    >
      {label}
    </ButtonBase>
  );
};

const UnitsBox: FC<PropsWithChildren> = ({ children }) => {
  return (
    <Box
      display="grid"
      gap={1 / 4}
      alignContent="start"
      sx={{ scrollbarWidth: 'thin', overflowY: 'auto', width: '100%' }}
    >
      {children}
    </Box>
  );
};
